import React, { useContext, useEffect, useMemo, useState } from "react";
import { WorksheetsEnvironment } from "printableWorksheets";
import { fetchAllSearchNodes, fetchNodesByIds, IFetchAllSearchNodesParams, IFetchNodesByIdParams, fetchSavedWorksheets, getWorksheetsByEntityIds, getUserDownloadList, getUserDownloadsCount, getUserSavedCount } from "./api";
import { fetchSubjectFromSignupUrl, hasSignedUpFromWs, isFlutterUser } from "apps/teacher/config/common";
import { commonDate } from "apps/teacher/config/commonDate";

const WS_FILTERS_LOCAL_STORAGE_KEY = `wsFilterData`;

export enum Grades {
    ALL_GRADES = 'All',
    PK = 'Pre-K',
    K = 'K',
    ONE = '1',
    TWO = '2',
    THREE = '3',
    FOUR = '4',
    FIVE = '5'
}

export enum SubjectCodes {
    MATH = 'math',
    ELA = 'ela',
}

export enum PRINTABLE_WORKSHEET_SCREENS {
    DEFAULT,
    SEARCH, 
    SAVED,
    DOWNLOADED
}

export const GradeToStringMapping: any = {
    [Grades.ALL_GRADES]: 'All Grades',
    [Grades.PK]: 'Pre-Kindergarteners',
    [Grades.K]: 'Kindergarteners',
    [Grades.ONE]: '1st Graders',
    [Grades.TWO]: '2nd Graders',
    [Grades.THREE]: '3rd Graders',
    [Grades.FOUR]: '4th Graders',
    [Grades.FIVE]: '5th Graders',
}

interface IPrintableWorksheetState {
    activeGrade: Grades;
    activeNode: any;
    activeWorksheet: any;
    worksheetSearchValue: string;
    subjectCode: string | null;
    activeBreadcrumb: Array<any>;
    showViewHeader: boolean;
    userId: string | null;
    worksheetsEnvironment: WorksheetsEnvironment | null;
    viewData: any;
    treeData: any;
    onboardingGrade: string;
    showHeader: boolean;
    savedWorksheets: any;
    savedEntityWorksheets: Array<any>;
    downloadedWorksheets: Array<any>;
    searchScreenData: any;
    defaultSearch: boolean;
    activeScreen: PRINTABLE_WORKSHEET_SCREENS,
    savedCount: number;
    isWorksheetsLoading: boolean;
    downloadCount: number;
    envProps: any;
    ineligibleGrade: boolean;
    filteredSearchTerm: string;
    allWorksheetsScreenData: any;
    setActiveGrade: (grade: Grades) => void;
    setActiveNode: (val: any) => void;
    setActiveWorksheet: (worksheet: any) => void;
    setWorksheetSearchValue: (str: string) => void;
    setSubjectCode: (str: string) => void;
    setActiveBreadcrumb: (crumb: any) => void;
    toggleViewHeader: (value: boolean) => void;
    setShowHeader: (val: boolean) => void;
    setSavedWorksheets: (val: any) => void;
    getSavedWorksheets: () => Promise<void>;
    getDownloadedWorksheets: (selectedDays: string) => Promise<void>;
    setSearchScreenData: (val: any) => void;
    setActiveScreen: (val: PRINTABLE_WORKSHEET_SCREENS) => void;
    setDefaultSearch: (val: boolean) => void;
    setSavedCount: (val: number | ((val: number) => number)) => void;
    setDownloadCount: (val: number | ((val: number) => number)) => void;
    setDownloadedWorksheets: (val: any[] | ((val: any[]) => any[])) => void;
    setSavedEntityWorksheets: (val: any[] | ((val: any[]) => any[])) => void;
    setFilteredSearchTerm: (filteredSearchTerm: string) => void;
    setAllWorksheetsScreenData: (val: any) => void;
 };

declare let window: any;

const PrintableWorksheetsContext = React.createContext({} as IPrintableWorksheetState);

const PrintableWorksheetsProvider = ({ userId, worksheetsEnvironment, onboardingGrade, children, subject = SubjectCodes.MATH, isGradeEligibleForEla, ...envProps }: any) => {

    const mapOnboardingGradeWithGrades = (grade: any) => {
        let Grade: any = Object.keys(Grades).find(grade=>Grades[grade] === onboardingGrade?.toUpperCase());
        return Grade ? Grades[Grade] : Grades.ALL_GRADES 
    }

    const fromOnboardingFlow = hasSignedUpFromWs() && JSON.parse(localStorage.getItem("showFilteredWsData"));
    let isFromOnboardingFlow = isFlutterUser() ? window.parent.parent.isFromOnboardingFlow : window.isFromOnboardingFlow;


    const activeGradeAndSubject = useMemo(() => {
        let gradeToSet: any = null;
        let subjectToSet: any = null;
        // @ts-ignore
        if(fromOnboardingFlow || isFromOnboardingFlow) {
            gradeToSet = localStorage.getItem('gradeSelectedOnWs') || 'All';
            subjectToSet = fetchSubjectFromSignupUrl();
        } else {
            // @ts-ignore
            if(!isFromOnboardingFlow) {
                const filtersFromLocalStorageStr = localStorage?.getItem(WS_FILTERS_LOCAL_STORAGE_KEY);
                const filtersFromLocalStorage = JSON.parse(filtersFromLocalStorageStr || "{}");
                if(Object.keys(filtersFromLocalStorage).length > 0 && filtersFromLocalStorage.userId == userId){
                    gradeToSet = filtersFromLocalStorage.activeGrade;
                    subjectToSet = envProps?.explicitSubject ? subject : filtersFromLocalStorage.subjectCode;
                } else {
                    gradeToSet = mapOnboardingGradeWithGrades(onboardingGrade);
                    subjectToSet = subject;
                }
            }
            if(worksheetsEnvironment == WorksheetsEnvironment.TEACHER_DASHBOARD){
                gradeToSet = mapOnboardingGradeWithGrades(onboardingGrade);
                subjectToSet = subject;
            }
        }

        return {
            gradeToSet,
            subjectToSet
        };
    }, [fromOnboardingFlow, onboardingGrade, subject]);

    const [activeGrade, setActiveGrade] = useState<Grades | null>(activeGradeAndSubject.gradeToSet);
    const [subjectCode, setSubjectCode] = useState<string | null>(activeGradeAndSubject.subjectToSet);
    const [activeNode, setActiveNode] = useState<any>(null);
    const [activeWorksheet, setActiveWorksheet] = useState<any>(null);
    const [treeData, setTreeData] = useState<any>(null);
    const [ineligibleGrade, setIneligibleGrade] = useState<boolean>(false);
    const [viewData, setViewData] = useState<any>(null);
    const [worksheetSearchValue, setWorksheetSearchValue] = useState<string>('');
    const [activeBreadcrumb, setActiveBreadcrumb] = useState<Array<any>>([]);
    const [showViewHeader, toggleViewHeader] = useState<boolean>(true);
    const [showHeader, setShowHeader] = useState<boolean>(false);
    const [savedWorksheets, setSavedWorksheets] = useState<any>({});
    const [savedEntityWorksheets, setSavedEntityWorksheets] = useState<Array<string>>([]);
    const [downloadedWorksheets, setDownloadedWorksheets] = useState<Array<any>>([]);
    const [searchScreenData, setSearchScreenData] = useState<any>(null);
    const [activeScreen, setActiveScreen] = useState<PRINTABLE_WORKSHEET_SCREENS>(PRINTABLE_WORKSHEET_SCREENS.DEFAULT);
    const [defaultSearch, setDefaultSearch] = useState<boolean>(true);
    const [savedCount, setSavedCount] = useState<number>(0);
    const [downloadCount, setDownloadCount] = useState<number>(0);
    const [isWorksheetsLoading, toggleIsWorksheetsLoading] = useState<boolean>(false);
    const [filteredSearchTerm, setFilteredSearchTerm] = useState<string>('');
    const [allWorksheetsScreenData, setAllWorksheetsScreenData] = useState<any>({});
    const [filtersLoadedFromLocalStorage, setFiltersLoadedLoadedFromLocalStorage] = useState<boolean>(false);

    const getSortedChildren = (children: Array<any>) => {
        const res = children.sort((a: any, b: any) => {
            let title1 = a.title.toLowerCase(),
                title2 = b.title.toLowerCase();

            if (title1 < title2) {
                return -1;
            }
            if (title1 > title2) {
                return 1;
            }
            return 0;
        });
        return res;
    }

    const sortChild = (child: any) => {
        if(child?.children?.length === 0){
            return child;
        } else {
            child.children = getSortedChildren(child?.children);
            child.children.map((child: any) => sortChild(child))
            return child;
        }
    }

    const getAllSearchNodes = async (grade: Grades): Promise<void> => {
        let isFromOnboardingFlow = isFlutterUser() ? window.parent.parent.isFromOnboardingFlow : window.isFromOnboardingFlow;
        toggleIsWorksheetsLoading((curr) => !curr);
        const paramsMath: IFetchAllSearchNodesParams = {
            gradeCode: grade,
            subjectCode: SubjectCodes.MATH
        };

        const paramsELA: IFetchAllSearchNodesParams = {
            gradeCode: grade,
            subjectCode: SubjectCodes.ELA
        };

        const promises = [fetchAllSearchNodes(paramsMath), fetchAllSearchNodes(paramsELA)];

        await Promise.all(promises).then(([mathRes, elaRes]: any) => {
            const sortedMathRes = sortChild(mathRes);
            const sortedElaRes = sortChild(elaRes);
            
            setTreeData([sortedElaRes, sortedMathRes]);
            // setActiveNode(elaRes);
            let initialElement: any = subject === SubjectCodes.MATH ? sortedMathRes : sortedElaRes;

            // @ts-ignore
            if(fromOnboardingFlow || isFromOnboardingFlow) {
                if(isFlutterUser()) {
                    window.parent.parent.isFromOnboardingFlow = true;
                }
                else {
                    window.isFromOnboardingFlow = true;
                }
                let initialData = fetchSubjectFromSignupUrl() === SubjectCodes.MATH ? sortedMathRes : sortedElaRes
                let activeBreadcrumbIds = JSON.parse(localStorage.getItem("searchNodeIds"));
                const data = [initialData];
                const activeBreadcrumbData = fetchDataForActiveBreadcrumb(data ,activeBreadcrumbIds, initialElement);
                setActiveBreadcrumb(activeBreadcrumbData);
                localStorage.removeItem("showFilteredWsData");
                if(isFlutterUser()) {
                    window.parent.parent.isFromOnboardingFlow = false;
                }
                else {
                    window.isFromOnboardingFlow = false;
                }
                persistFilterData(activeBreadcrumbData);
            }
            else {
                // @ts-ignore
                if(!isFromOnboardingFlow && worksheetsEnvironment == WorksheetsEnvironment.STUDENT_DASHBOARD) {
                    const filtersFromLocalStorageStr = localStorage?.getItem(WS_FILTERS_LOCAL_STORAGE_KEY);
                    const filtersFromLocalStorage = JSON.parse(filtersFromLocalStorageStr || "{}");
                    if(!envProps?.explicitSubject && Object.keys(filtersFromLocalStorage).length > 0 &&  !filtersLoadedFromLocalStorage && filtersFromLocalStorage.userId == userId){
                        setActiveBreadcrumb(filtersFromLocalStorage.activeBreadcrumb?.length > 0 ? filtersFromLocalStorage.activeBreadcrumb : [initialElement]);
                    } else {
                        setActiveBreadcrumb([initialElement]);
                    }
                    setFiltersLoadedLoadedFromLocalStorage(true);
                } else {
                    setActiveBreadcrumb([initialElement]);
                }
            }
        }).finally(() => toggleIsWorksheetsLoading((curr) => !curr));
    }

    const persistFilterData = (explicitActiveBreadCrumbData: any = null) => {
        const dataToStore = {
            userId,
            activeBreadcrumb: explicitActiveBreadCrumbData ? explicitActiveBreadCrumbData : activeBreadcrumb,
            activeGrade,
            subjectCode: activeBreadcrumb?.[0]?.title == 'ELA' ? SubjectCodes.ELA : SubjectCodes.MATH
        }
        localStorage.setItem(WS_FILTERS_LOCAL_STORAGE_KEY, JSON.stringify(dataToStore));
    }

    useEffect(() => {
        filtersLoadedFromLocalStorage && persistFilterData();
    }, [activeBreadcrumb, activeGrade, subjectCode])

    const getNodeContentById = async (nodeIds: Array<number>): Promise<void> => {
        
        const params: IFetchNodesByIdParams = {
            searchNodeIds: nodeIds,
            pageNumber: 0,
            pageSize: 10,
        }

        await fetchNodesByIds(params).then((res: any) => {
            let dataToSet: any = [];
            Object.keys(res.search_contents).forEach((nodeId: string) => {
                const worksheets = res.search_contents[nodeId];
                const match = activeNode?.children?.find((node: any) => node.id == nodeId);
                const title = match ? match.section_title : "";
                const playblesCount = match ? match.playable_count : 0;
                dataToSet.push({title, nodeId, worksheets, playblesCount});
            });
            dataToSet = getSortedChildren(dataToSet);
            setViewData(dataToSet);
        });
    }

    const getUserSavedCollection = async (pageNumber = 0, pageSize = 1000) => {
        if (userId) {
            await fetchSavedWorksheets({
                userId,
                pageNumber,
                pageSize,
            }).then(async (result = {}) => {
                const data = result.user_collection_entities || [];
                let tempSavedWorksheets = data.reduce((acc: any, item: any) => {
                    acc[item.entity_id] = item;
                    return acc;
                }, {})
                setSavedWorksheets(tempSavedWorksheets)
            });
        }
    }

    const getSavedWorksheets = async (): Promise<void> => {
        const savedWorksheetIds = Object.keys(savedWorksheets);
        if(savedWorksheetIds?.length > 0) {
            const params = {
                ids: savedWorksheetIds,
            }
            const res = await getWorksheetsByEntityIds({params});
            setSavedEntityWorksheets(res);
        }   
    };

    const calculateDateFromToday = (days: any) => {
        return commonDate.CalculateDateFromToday(days);
    }

    const getDownloadedWorksheets = async (selectedDays: string): Promise<void> => {
        let params = {
            page_number: 0,
            page_size: 1000,
            created_date_sort: "desc",
            ...((selectedDays !== "365") ? { created_date_gte: calculateDateFromToday(parseInt(selectedDays)-1) } : {})
        }
        const downloadedEntities: Array<any> = await getUserDownloadList({...params, userId});
        
        if(downloadedEntities?.length > 0){
            const params: any = {
                ids: downloadedEntities?.map((entity: any) => entity.entity_id)
            }
            const res = await getWorksheetsByEntityIds({params});
            const sortedRes: any = [];
            downloadedEntities.forEach((entity: any) => {
                res.find((worksheet: any) => {
                    if(worksheet?.id === entity.entity_id){
                        sortedRes.push(worksheet);
                    }
                });
            });
            setDownloadedWorksheets(sortedRes);     
        } else {
            setDownloadedWorksheets([]);
        }
    }

    const initTeacherWorksheetsData = () => {
        getUserSavedCollection();
        getDownloadedWorksheets("7");
        getUserDownloadsCount({userId}).then((res) => {
            setDownloadCount(res);
        });
        getUserSavedCount({userId}).then((res) => {
            setSavedCount(res);
        })
    }

    const fetchDataForActiveBreadcrumb = (data: any, ids: any, initialElement: any) => {
        let result: any = [];
        let idsUpdated: any = [];
        idsUpdated = ids.length > 0 && ids.reverse();
        const recursiveFilter = (arr: any) => {
          for (let item of arr) {
            if (idsUpdated.includes(item.id)) {
              result.push(item);
            }
            if (item.children && item.children.length > 0) {
              recursiveFilter(item.children);
            }
          }
        }

        if(data.length > 0 && idsUpdated.length > 0) {
            recursiveFilter(data);
        }
        return result.length > 0 ? result : [initialElement];
    }

    useEffect(()=> {
        if(activeNode?.id && activeNode?.children?.length > 0){
            getNodeContentById(activeNode?.children.map((node: any) => node.id));
        }
    }, [activeNode?.id]);

    useEffect(() => {
        if(activeBreadcrumb?.length > 0){
            let lastBreadCrumb = activeBreadcrumb[activeBreadcrumb.length - 1];
            setActiveNode(lastBreadCrumb);
        }
    }, [activeBreadcrumb]);

    useEffect(() => {
        if(activeGrade && subjectCode){
            setTreeData([]);
            getAllSearchNodes(activeGrade);
        }
    }, [activeGrade, subjectCode])

    useEffect(() => {
        // In case ela grade change is required
        if (subject === SubjectCodes.ELA) {
            if (isGradeEligibleForEla && !isGradeEligibleForEla(activeGrade?.toLowerCase())) {
                setIneligibleGrade(true)
            } else {
                setIneligibleGrade(false)
            }
        } else {
            setIneligibleGrade(false)
        }
        if(worksheetsEnvironment == WorksheetsEnvironment.TEACHER_DASHBOARD){
            setSubjectCode(subject)
        }
    }, [subject, activeGrade])

    useEffect(() => {
        worksheetsEnvironment === WorksheetsEnvironment.TEACHER_DASHBOARD ?  setActiveGrade(onboardingGrade?.toUpperCase()) : null;
    }, [onboardingGrade])

    useEffect(()=> {
        worksheetsEnvironment === WorksheetsEnvironment.TEACHER_DASHBOARD ? initTeacherWorksheetsData() : null;
    }, [userId])

    const PrintableWorksheetsState: IPrintableWorksheetState = {
        activeGrade,
        activeNode,
        activeWorksheet,
        worksheetSearchValue,
        subjectCode,
        activeBreadcrumb,
        showViewHeader,
        userId,
        worksheetsEnvironment,
        treeData,
        viewData,
        onboardingGrade,
        showHeader,
        savedWorksheets,
        savedEntityWorksheets,
        downloadedWorksheets,
        searchScreenData,
        activeScreen,
        defaultSearch,
        savedCount,
        downloadCount,
        isWorksheetsLoading,
        envProps,
        ineligibleGrade,
        filteredSearchTerm,
        allWorksheetsScreenData,
        setActiveGrade,
        setActiveNode,
        setActiveWorksheet,
        setWorksheetSearchValue,
        setSubjectCode,
        setActiveBreadcrumb,
        toggleViewHeader,
        setShowHeader,
        setSavedWorksheets,
        getSavedWorksheets,
        getDownloadedWorksheets,
        setSearchScreenData,
        setActiveScreen,
        setDefaultSearch,
        setSavedCount,
        setDownloadCount,
        setDownloadedWorksheets,
        setSavedEntityWorksheets,
        setFilteredSearchTerm,
        setAllWorksheetsScreenData
    };

    return (
        <PrintableWorksheetsContext.Provider value={PrintableWorksheetsState}>
            { children } 
        </PrintableWorksheetsContext.Provider>
    );
}

export const usePrintableWorksheetsState = () => {
    const context = useContext(PrintableWorksheetsContext);
    if (!context) {
        throw Error(`Use ${usePrintableWorksheetsState.name} in ${PrintableWorksheetsContext.displayName}`);
    }
    return context;
};

export default PrintableWorksheetsProvider;